import HBMessage from "@/components/HBMessage";
import { BASE_URL } from "@/constants/apiConfig";
import axios, {
  AxiosRequestConfig,
  type AxiosError,
  type AxiosResponse,
} from "axios";
import { isString, omit } from "lodash";
import { gdSlsReportError } from "../gdSlsRum";

/**
 * 标准化错误信息
 */
export interface NormalizedError {
  /**错误码，后端接口返回的code或者err值，已经其他场景自定义的code */
  code: string | number;
  /**用于用户提示的错误消息，前端会做友好化处理 */
  message: string;
  /**开发调试信息, 用于错误日志上报 */
  devMessage: string;
  /**原始axios响应 */
  response?: AxiosResponse;
  /**原始请求配置 */
  config?: AxiosRequestConfig & {
    options?: ICustomConfigOption;
    accessToken?: string;
    retryTimes?: number;
  };

  /**错误码，花瓣后端返回的，用于兼容老的代码处理 */
  err?: string | number;
  /**错误消息，花瓣后端返回的，用于兼容老的代码处理 */
  msg?: string;
  /**验证码key，用于兼容老的代码处理 */
  captchakey?: string;
}

/**自定义配置选项 */
interface ICustomConfigOption {
  /**是否忽略错误上报 */
  ignoreError?: boolean;
  /**是否忽略错误提示 */
  ignoreMessage?: boolean;
  /**是否返回原始响应数据 */
  returnRes?: boolean;
  /**是否忽略稿定租户权限问题处理 */
  ignoreGdPermission?: boolean;
  /**是否忽略人机验证处理 */
  ignoreVerify?: boolean;
  /**请求函数, 用于重放请求 */
  request?: (config: AxiosRequestConfig) => Promise<any>;
  /**accessToken */
  accessToken?: string;
  /**请求重试次数 */
  times?: number;
}

/**HTTP状态码对应的错误消息映射, 用于处理非友好提示时错误消息 */
const HTTP_STATUS_MAP = {
  "400": "请求参数错误，请检查输入信息",
  "401": "登录已过期，请重新登录",
  "403": "权限不足，请联系管理员",
  "404": "请求的资源不存在",
  "500": "服务器内部错误，请稍后重试",
  "502": "服务暂时不可用，请稍后重试",
  "503": "服务维护中，请稍后重试",
};

/**
 * 错误处理器
 * @example
 * ```ts
 * const errorHandler = new ErrorHandler();
 * errorHandler.registerCustomHandler();
 * errorHandler.registerCustomHandler(handlePermission);
 * ```
 */
export default class ErrorHandler {
  private handlers: Array<(error: AxiosError) => NormalizedError | undefined> =
    [];
  private customHandlers: Array<(error: NormalizedError) => NormalizedError> =
    [];

  constructor() {
    // 默认注册的核心错误处理器
    this.registerHandler(this.handleNetworkError);
    this.registerHandler(this.handleStringError);
    this.registerHandler(this.handleApiError);
  }

  private registerHandler(
    handler: (error: AxiosError) => NormalizedError
  ): void {
    this.handlers.push(handler);
  }

  public async handler(error: AxiosError): Promise<NormalizedError> {
    let normalized: NormalizedError | undefined;

    for (const fn of this.handlers) {
      normalized = fn.call(this, error);
      if (normalized) break;
    }

    normalized.response = error.response;
    normalized.config = error.config;

    const customResult = await this.applyCustomHandlers(normalized!);

    // 如果自定义处理函数返回了结果，则直接返回
    // 比如：用户不在租户里，自动进入租户，然后重发请求，并返回此请求的响应
    if (customResult) {
      return customResult;
    }

    if (normalized) {
      // @ts-ignore
      const options = error.config?.options as ICustomConfigOption;
      const ignoreError = options?.ignoreError;
      if (!ignoreError) {
        this.reportError(normalized);
      }

      const ignoreMessage = options?.ignoreMessage;
      if (!ignoreMessage && !axios.isCancel(error)) {
        isString(normalized.message) &&
          HBMessage.error({
            content: normalized.message,
            key: "interface-error-message",
            duration: 3,
          });
      }

      // 花瓣接口错误，直接抛出错误，兼容老的处理模式
      if (
        error.config.baseURL === BASE_URL &&
        error.response?.status !== 200 &&
        !ignoreError
      ) {
        return Promise.reject(omit(normalized, ["response", "config"]));
      }
    }

    // 移除response和config，避免服务端渲染场景序列化报错
    return omit(normalized, ["response", "config"]);
  }

  public registerCustomHandler(handler: (error: NormalizedError) => any): void {
    this.customHandlers.push(handler);
  }

  private async applyCustomHandlers(
    error: NormalizedError
  ): Promise<NormalizedError> {
    for (const fn of this.customHandlers) {
      const result = await fn.call(this, error);

      if (result) {
        // 如果处理函数返回了结果，则直接返回
        return result;
      }
    }
  }

  private handleNetworkError(error: AxiosError): NormalizedError {
    let code;
    let message;

    if (error.message?.includes("Network Error")) {
      code = "NETWORK_ERROR";
      message = "网络错误，请重试或检查网络状态";
    }

    if (error.message?.includes("timeout")) {
      code = "TIMEOUT";
      message = "请求超时，请重试或检查网络状态";
    }

    if (axios.isCancel(error)) {
      code = "CANCELLED";
      message = "请求已取消";
    }

    if (code) {
      return {
        err: code,
        msg: message,
        code,
        message,
        devMessage: message,
      };
    }
  }

  /**
   * 处理返回后端有响应，但返回是一串字符串场景，比如WAF拦截等
   * @param error
   */
  private handleStringError(error: AxiosError): NormalizedError {
    let code;
    let message;

    if (isString(error.response?.data)) {
      const errStr = error.response?.data as string;

      const isHtml = errStr.includes("<html>");

      if (isHtml && error.response?.status === 405) {
        code = "WAF_ERROR";
        message = "服务暂时出现问题，请稍后再试";
      } else if (isHtml) {
        code = "HTML_ERROR";
        message = "服务暂时出现问题，请稍后再试";
      } else {
        code = "STRING_ERROR";
        // 如果错误消息不是用户友好消息，则使用HTTP状态码对应的错误消息
        if (!this.isUserFriendlyMessage(errStr)) {
          message = HTTP_STATUS_MAP[error.response?.status];
        } else {
          message = errStr;
        }
      }

      return {
        err: code,
        msg: message,
        code,
        message,
        devMessage: message,
      };
    }
  }

  /**
   * 处理json格式错误
   * @param error
   * @returns
   */
  private handleApiError(error: AxiosError): NormalizedError {
    const res =
      (error.response?.data as {
        err?: string;
        msg?: string;
        message?: string;
        code?: string;
        error?: string;
      }) || {};

    // 非json对象，直接返回
    if (!(res instanceof Object)) {
      return;
    }

    let message, code;
    if (res.err || res.error) {
      // 花瓣返回错误格式
      message = res.msg || res.message || res.error;
      code = res.err || res.error;
    } else if (res.code) {
      // 稿定返回格式
      message = res.message;
      code = res.code;
    }

    // 如果错误消息不是用户友好消息，则使用HTTP状态码对应的错误消息
    if (!this.isUserFriendlyMessage(message)) {
      gdSlsReportError("Api Error: 消息不是用户友好消息", {
        status: error.response?.status,
        message: error.response?.data,
        url: error.config?.url,
      });

      message = HTTP_STATUS_MAP[error.response?.status] || message;
    }

    return {
      err: code,
      msg: message,
      code,
      message,
      devMessage: message,
    };
  }

  private reportError(error: NormalizedError): void {
    // 404不上报，接口太多（采集不存在、用户不存在等） pins/:id,boards/:id user/me
    if (error.response?.status === 404 || error.code === 404) {
      return;
    }

    console.error(
      // 时间
      new Date().toISOString(),
      "接口错误",
      error.config?.url,
      error.devMessage || error.message
    );
    if (error.response?.status > 400 && error.response?.data) {
      // 400 以上错误sls会自动记录上报，无需手动再上报
      return;
    }

    // 其他类型错误记录一下
    gdSlsReportError(error.devMessage || error.message, {
      status: error.response?.status,
      message: error.response?.data,
      url: error.config?.url,
    });
  }

  private isUserFriendlyMessage(msg: string): boolean {
    if (!msg) return false;

    // 1. 检查长度
    if (msg.length < 2 || msg.length > 50) return false;

    // 2. 排除技术错误关键词（可根据项目扩展）
    const techKeywords = [
      "ECONN",
      "ERR_",
      "Request failed",
      "Network Error",
      "timeout",
      "stack",
      "at ",
      "Exception",
      "ReferenceError",
      "TypeError",
    ];
    if (techKeywords.some((k) => msg.includes(k))) return false;

    // 3. 检查是否主要由可读字符组成（中文/字母/数字/常用符号）
    const readablePattern =
      /^[\u4e00-\u9fa5_a-zA-Z0-9\s，,。!！？?【】（）()、:：“”‘’]+$/;
    if (!readablePattern.test(msg)) return false;

    return true;
  }
}
